// wrap js window object
/** @public */
export interface CustomWindow extends Window {
  ENV: RuntimeConfig;
  ENV_HOST: RuntimeHostConfig;
}

declare let window: CustomWindow;

// declare environment variables
const RunTimeVariable = {
  AUTH0_AUDIENCE: "AUTH0_AUDIENCE",
  AUTH0_CALLBACK_ROUTE: "AUTH0_CALLBACK_ROUTE",
  AUTH0_CLIENT_ID: "AUTH0_CLIENT_ID",
  AUTH0_DATABASE_CONNECTION_NAME: "AUTH0_DATABASE_CONNECTION_NAME",
  AUTH0_DOMAIN: "AUTH0_DOMAIN",
  AUTH0_LEGACY_CLAIMS_NAMESPACE: "AUTH0_LEGACY_CLAIMS_NAMESPACE",
  AUTH0_SCOPES: "AUTH0_SCOPES",
  AUTO_UPDATE_INTERVAL: "AUTO_UPDATE_INTERVAL",
  ENVIRONMENT: "ENVIRONMENT",
  LAUNCHDARKLY_CLIENT_ID: "LAUNCHDARKLY_CLIENT_ID",
  MIXPANEL_TOKEN: "MIXPANEL_TOKEN",
  MIXPANEL_REGION: "MIXPANEL_REGION",
  SENTRY_ENABLED: "SENTRY_ENABLED",
  TIP_TAP_AI_CONFIG: "TIP_TAP_AI_CONFIG",
  MUI_X_LICENSE_KEY: "MUI_X_LICENSE_KEY",
  AMPLIFY_DASHBOARD_TEMPLATE_BASE_URL: "AMPLIFY_DASHBOARD_TEMPLATE_BASE_URL",
  OTEL_EXPORTER_OTLP_ENDPOINT: "OTEL_EXPORTER_OTLP_ENDPOINT",
  TENANCY_UI_ENABLED: "TENANCY_UI_ENABLED",
} as const;

type TipTapAiConfig = {
  /** should be a supported OpenAITextModel, see:
   * - https://tiptap.dev/docs/editor/ai/extension/commands#text-options
   * - @tiptap-pro/extension-ai/dist/tiptap-pro/packages/extension-ai/src/types.d.ts
   */
  modelName?: string;
  conceptIdeaPrompt?: string;
  optimizeBriefPrompt?: string;
};

type RuntimeConfig = {
  AUTH0_AUDIENCE: string;
  AUTH0_CALLBACK_ROUTE: string;
  AUTH0_CLIENT_ID: string;
  AUTH0_DATABASE_CONNECTION_NAME: string;
  AUTH0_DOMAIN: string;
  AUTH0_LEGACY_CLAIMS_NAMESPACE: string;
  AUTH0_SCOPES: string;
  AUTO_UPDATE_INTERVAL: number | false | undefined;
  DOCUMENT_BASE_URL: string;
  ENVIRONMENT: string;
  LAUNCHDARKLY_CLIENT_ID: string;
  MIXPANEL_TOKEN?: string;
  MIXPANEL_REGION?: string;
  SENTRY_ENABLED: boolean;
  TIP_TAP_AI_CONFIG: TipTapAiConfig;
  MUI_X_LICENSE_KEY: string;
  AMPLIFY_DASHBOARD_TEMPLATE_BASE_URL: string;
  OTEL_EXPORTER_OTLP_ENDPOINT: string | undefined;
  TENANCY_UI_ENABLED: boolean | undefined;
};

type RuntimeHostConfig = {
  AUTH0_ORGANIZATION_ID?: string;
  DOCUMENT_BASE_URL: string;
  HUBSPOT_CONCEPT_QUALITY_CTA_ID?: string;
  HUBSPOT_NPS_CTA_ID?: string;
  HUBSPOT_NPS_TRIGGER_PROBABILITY?: number;
  HUBSPOT_TRACKING_PORTAL_ID?: string;
  SUPERGRAPH_BASE_URL: string;
};

const apiUrl = (name: string, fallback: string) =>
  process.env.BACKEND_PROXY || !fallback
    ? `${window.location.protocol}//${[window.location.host, name].filter(Boolean).join("/")}/graphql`
    : fallback;

/** Centralises the way we retrieve environment configuration throughout the app */
export const ConfigService = Object.freeze({
  APP_URL: () => window.location.origin,
  /** The full commit SHA the app was built from, read from `index.html`'s `<meta name="release">`. */
  APP_VERSION: () =>
    document.querySelector<HTMLMetaElement>('meta[name="release"]')?.content || "unknown",
  AUTH0_AUDIENCE: () => window.ENV[RunTimeVariable.AUTH0_AUDIENCE],
  AUTH0_CALLBACK_ROUTE: () => window.ENV[RunTimeVariable.AUTH0_CALLBACK_ROUTE],
  AUTH0_CLIENT_ID: () => window.ENV[RunTimeVariable.AUTH0_CLIENT_ID],
  AUTH0_DATABASE_CONNECTION_NAME: () => window.ENV[RunTimeVariable.AUTH0_DATABASE_CONNECTION_NAME],
  AUTH0_DOMAIN: () => window.ENV[RunTimeVariable.AUTH0_DOMAIN],
  AUTH0_LEGACY_CLAIMS_NAMESPACE: () => window.ENV[RunTimeVariable.AUTH0_LEGACY_CLAIMS_NAMESPACE],
  AUTH0_ORGANIZATION_ID: () => window.ENV_HOST.AUTH0_ORGANIZATION_ID?.trim() || undefined,
  AUTH0_SCOPES: () => window.ENV[RunTimeVariable.AUTH0_SCOPES] || "",
  AUTH0_SUPERGRAPH_URL: () => {
    if (process.env.BACKEND_PROXY) {
      const separator = process.env.BACKEND_PROXY.includes(".preview") ? "--" : ".";
      return `https://api${separator}${process.env.BACKEND_PROXY}.shuttlerock.com/graphql`;
    }

    return window.location.hostname === "localhost"
      ? undefined
      : apiUrl("", window.ENV_HOST.SUPERGRAPH_BASE_URL);
  },
  AUTO_UPDATE_INTERVAL: () => window.ENV[RunTimeVariable.AUTO_UPDATE_INTERVAL],
  DOCUMENT_BASE_URL: () => window.ENV_HOST.DOCUMENT_BASE_URL,
  ENVIRONMENT: () => window.ENV[RunTimeVariable.ENVIRONMENT],
  HUBSPOT_CONCEPT_QUALITY_CTA_ID: () => window.ENV_HOST.HUBSPOT_CONCEPT_QUALITY_CTA_ID || "",
  HUBSPOT_NPS_CTA_ID: () => window.ENV_HOST.HUBSPOT_NPS_CTA_ID || "",
  HUBSPOT_NPS_TRIGGER_PROBABILITY: () => window.ENV_HOST.HUBSPOT_NPS_TRIGGER_PROBABILITY ?? 0,
  HUBSPOT_TRACKING_PORTAL_ID: () => window.ENV_HOST.HUBSPOT_TRACKING_PORTAL_ID || "",
  LAUNCHDARKLY_CLIENT_ID: () => window.ENV[RunTimeVariable.LAUNCHDARKLY_CLIENT_ID],
  MIXPANEL_TOKEN: () => window.ENV[RunTimeVariable.MIXPANEL_TOKEN]?.trim() || undefined,
  MIXPANEL_REGION: () => window.ENV[RunTimeVariable.MIXPANEL_REGION]?.trim() || undefined,
  SENTRY_ENABLED: () => window.ENV[RunTimeVariable.SENTRY_ENABLED],
  SUPERGRAPH_BASE_URL: () => apiUrl("", window.ENV_HOST.SUPERGRAPH_BASE_URL),
  TIP_TAP_AI_CONFIG: () => window.ENV[RunTimeVariable.TIP_TAP_AI_CONFIG],
  MUI_X_LICENSE_KEY: () => window.ENV[RunTimeVariable.MUI_X_LICENSE_KEY],
  AMPLIFY_DASHBOARD_TEMPLATE_BASE_URL: () =>
    window.ENV[RunTimeVariable.AMPLIFY_DASHBOARD_TEMPLATE_BASE_URL],
  OTEL_EXPORTER_OTLP_ENDPOINT: () => window.ENV[RunTimeVariable.OTEL_EXPORTER_OTLP_ENDPOINT],
  // When enabled certain UI elements should be hidden and changed. These elements either aren't intended for use by
  // tenancy users, or aren't relevant for concept workflow.
  TENANCY_UI_ENABLED: () => window.ENV[RunTimeVariable.TENANCY_UI_ENABLED] || false,
});
